import { ViteEnv } from '@/env';

import { supabaseClient } from '../supabase';

const fetchWithToken = async (url: string, init: RequestInit, token: string) =>
  fetch(url, {
    ...init,
    headers: {
      ...init.headers,
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });

const internalFetch = (
  baseUrl: string,
): ((
  relativeUrl: string,
  init: RequestInit,
  authenticated?: boolean,
) => Promise<Response>) => {
  return async (
    relativeUrl: string,
    init: RequestInit,
    authenticated = true,
  ): Promise<Response> => {
    const url = `${baseUrl}${relativeUrl}`;

    if (!authenticated) {
      return fetch(url, {
        ...init,
        headers: {
          'Content-Type': 'application/json',
          ...init.headers,
        },
      });
    }

    const {
      data: { session },
    } = await supabaseClient.auth.getSession();

    if (!session) {
      throw new Error('No session found');
    }

    const response = await fetchWithToken(url, init, session.access_token);

    if (response.ok || response.status !== 401) {
      return response;
    }

    const {
      data: { session: newSession },
    } = await supabaseClient.auth.refreshSession(session);

    if (newSession === null) {
      return response;
    }

    return fetchWithToken(url, init, newSession.access_token);
  };
};

export const fetchBackend = internalFetch(ViteEnv.VITE_BACKEND_URL);
export const fetchSlackbot = internalFetch(ViteEnv.VITE_SLACKBOT_URL);

/**
 * GitLab-specific error handling utilities
 */
export const GitlabErrorCodes = {
  AUTHENTICATION_ERROR: 'GITLAB_AUTH_ERROR',
  PERMISSION_ERROR: 'GITLAB_PERMISSION_ERROR',
  REPOSITORY_NOT_FOUND: 'GITLAB_REPO_NOT_FOUND',
  WEBHOOK_NOT_FOUND: 'GITLAB_WEBHOOK_NOT_FOUND',
  WEBHOOK_ALREADY_EXISTS: 'GITLAB_WEBHOOK_EXISTS',
  RATE_LIMIT_EXCEEDED: 'GITLAB_RATE_LIMIT',
  NETWORK_ERROR: 'GITLAB_NETWORK_ERROR',
  UNKNOWN_ERROR: 'GITLAB_UNKNOWN_ERROR',
} as const;

const isAuthenticationError = (message: string): boolean =>
  message.includes('401') || message.includes('unauthorized');

const isPermissionError = (message: string): boolean =>
  message.includes('403') || message.includes('forbidden');

const isNotFoundError = (message: string): boolean =>
  message.includes('404') || message.includes('not found');

const isConflictError = (message: string): boolean =>
  message.includes('409') || message.includes('conflict');

const isRateLimitError = (message: string): boolean =>
  message.includes('429') || message.includes('rate limit');

const isNetworkError = (message: string): boolean =>
  message.includes('network') || message.includes('fetch');

const getErrorMessageForType = (message: string): string | null => {
  if (isAuthenticationError(message)) {
    return 'GitLab authentication failed. Please reconnect your GitLab account.';
  }

  if (isPermissionError(message)) {
    return 'You do not have permission to access this GitLab resource.';
  }

  if (isNotFoundError(message)) {
    return 'The requested GitLab resource was not found.';
  }

  if (isConflictError(message)) {
    return 'A webhook already exists for this repository.';
  }

  if (isRateLimitError(message)) {
    return 'GitLab API rate limit exceeded. Please try again later.';
  }

  if (isNetworkError(message)) {
    return 'Network error occurred. Please check your connection and try again.';
  }

  return null;
};

export const getGitlabErrorMessage = (error: unknown): string => {
  if (error instanceof Error) {
    const message = error.message.toLowerCase();
    const specificMessage = getErrorMessageForType(message);

    return specificMessage ?? error.message;
  }

  return 'An unexpected error occurred with GitLab integration.';
};

export const isGitlabTemporaryError = (error: unknown): boolean => {
  if (error instanceof Error) {
    const message = error.message.toLowerCase();

    return (
      message.includes('429') || // Rate limit
      message.includes('502') || // Bad gateway
      message.includes('503') || // Service unavailable
      message.includes('504') || // Gateway timeout
      message.includes('network') ||
      message.includes('timeout')
    );
  }

  return false;
};
